<script setup lang="ts">
import type { PlayerMatchmakerQueue } from '@/api/public'
import { queueAllowsPartySize } from '@/lib/playerSocialPresentation'

const props = defineProps<{
  open: boolean
  queues: PlayerMatchmakerQueue[]
  selectedQueueId: string
  partySize: number
  error: string | null
  selectedQueueNotice?: string
}>()

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'selectQueue', queueID: string): void
}>()

type MatchContract = PlayerMatchmakerQueue['active_contract']

const gameModeLabels: Record<number, string> = {
  1: 'All Pick',
  2: 'Captains Mode',
  3: 'Random Draft',
  4: 'Single Draft',
  5: 'All Random',
  11: 'Mid Only',
  16: 'Captains Draft',
  17: 'Balanced Draft',
  18: 'Ability Draft',
  20: 'All Random Death Match',
  21: '1v1 Mid Only',
  22: 'Ranked All Pick',
}

function gameModeLabel(contract: MatchContract): string {
  return gameModeLabels[contract.game_mode] || contract.label || contract.name || `Mode ${contract.game_mode}`
}

function queueTitle(queue: PlayerMatchmakerQueue): string {
  return queue.label.trim() || queue.id
}

function queueSubtitle(queue: PlayerMatchmakerQueue): string {
  return gameModeLabel(queue.active_contract)
}

function queueStatus(queue: PlayerMatchmakerQueue): string {
  if (queue.ban) return 'Blocked'
  if (!queue.enabled) return 'Disabled'
  if (queue.is_at_cap) return 'Full'
  if (!queueAllowsPartySize(queue, props.partySize)) {
    return `Группа ${props.partySize}: нельзя`
  }
  if (queue.queue_phase === 'READY_CHECKING') return 'Ready Check'
  return `${queue.queue_count}/${queue.active_contract.cap}`
}

function queueDisabled(queue: PlayerMatchmakerQueue): boolean {
  return (
    !queue.enabled ||
    queue.is_at_cap ||
    Boolean(queue.ban) ||
    !queueAllowsPartySize(queue, props.partySize)
  )
}

function queueInlineNotice(queue: PlayerMatchmakerQueue): string {
  if (queue.id !== props.selectedQueueId) return ''
  return props.selectedQueueNotice || props.error || ''
}

function activateTransitionElement(element: Element): void {
  if (element instanceof HTMLElement) element.inert = false
}

function deactivateTransitionElement(element: Element): void {
  if (element instanceof HTMLElement) element.inert = true
}
</script>

<template>
  <Transition
    name="match-search-backdrop"
    @before-enter="activateTransitionElement"
    @before-leave="deactivateTransitionElement"
  >
    <button
      v-if="open"
      class="match-search-backdrop"
      type="button"
      aria-label="Close match search"
      @click="emit('close')"
    ></button>
  </Transition>

  <Transition
    name="match-search-panel-slide"
    @before-enter="activateTransitionElement"
    @before-leave="deactivateTransitionElement"
  >
    <aside v-if="open" class="match-search-panel open">
      <div class="match-search-menu">
        <div class="match-search-queues" style="--search-order: 1">
          <button
            v-for="queue in queues"
            :key="queue.id"
            class="match-search-queue"
            :class="{ selected: queue.id === selectedQueueId, unavailable: queueDisabled(queue), banned: Boolean(queue.ban), 'has-notice': Boolean(queueInlineNotice(queue)) }"
            type="button"
            :disabled="queueDisabled(queue)"
            :aria-pressed="queue.id === selectedQueueId"
            @click="emit('selectQueue', queue.id)"
          >
            <span class="match-search-queue-head">
              <strong>{{ queueTitle(queue) }}</strong>
              <span v-if="queue.private" class="match-search-private-badge">Private</span>
              <span class="match-search-queue-status">{{ queueStatus(queue) }}</span>
            </span>
            <span class="match-search-mode-row">
              <span class="match-search-check"></span>
              <span class="match-search-mode-label">{{ queueSubtitle(queue) }}</span>
            </span>
            <span v-if="queueInlineNotice(queue)" class="match-search-queue-notice">
              {{ queueInlineNotice(queue) }}
            </span>
          </button>
        </div>
      </div>
    </aside>
  </Transition>
</template>
